import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { categories } from "@/mocks/site";

const navItems = [
  { label: "首页", to: "/" },
  ...categories.map((item) => ({ label: item.name, to: `/category/${item.key}` })),
  { label: "影视资讯", to: "/news" },
];

export default function SiteHeader() {
  const location = useLocation();
  const navigate = useNavigate();
  const [keyword, setKeyword] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setMenuOpen(false);
    setSearchOpen(false);
  }, [location.pathname, location.search]);

  const isActive = (to: string) => {
    if (to === "/") return location.pathname === "/";
    return location.pathname.startsWith(to);
  };

  const submitSearch = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = keyword.trim();
    if (!value) return;
    navigate(`/search?q=${encodeURIComponent(value)}`);
    setMenuOpen(false);
    setSearchOpen(false);
  };

  useEffect(() => {
    if (searchOpen) inputRef.current?.focus();
  }, [searchOpen]);

  return (
    <header className="sticky top-0 z-50 w-full border-b border-background-200 bg-background-50/95 backdrop-blur">
      <div className="hidden bg-primary-900 text-background-100 md:block">
        <div className="portal-container flex h-9 items-center justify-between text-xs">
          <p className="flex items-center gap-2 text-primary-100">
            <i className="ri-fire-line text-accent-400" aria-hidden="true" />
            色虎每日更新热门影视剧与行业资讯，演示数据仅供内容浏览
          </p>
          <div className="flex items-center gap-5 text-primary-200">
            <span className="flex items-center gap-1">
              <i className="ri-calendar-line" aria-hidden="true" />
              2025 秋季档热播中
            </span>
            <Link to="/news" className="transition-colors hover:text-accent-300">
              影视资讯
            </Link>
          </div>
        </div>
      </div>

      <div className="portal-container flex h-16 items-center justify-between gap-4 md:h-[72px]">
        <Link to="/" className="flex shrink-0 items-center gap-2.5" aria-label="色虎首页">
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-500 text-lg font-black text-background-50">
            虎
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-heading text-xl font-bold tracking-wide text-primary-800">色虎</span>
            <span className="mt-1 text-[10px] uppercase tracking-[0.22em] text-foreground-400">Sehu Film</span>
          </span>
        </Link>

        <nav className="hidden flex-1 items-center justify-center gap-7 lg:flex" aria-label="主导航">
          {navItems.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className={`nav-link ${isActive(item.to) ? "text-accent-600" : ""}`}
            >
              {item.label}
              {isActive(item.to) && (
                <span className="absolute -bottom-2 left-1/2 h-[3px] w-4 -translate-x-1/2 rounded-full bg-accent-500" />
              )}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <form onSubmit={submitSearch} className="hidden md:block" role="search">
            <div className="flex h-9 w-[210px] items-center gap-2 rounded-md border border-background-300 bg-background-100 px-3 transition-colors focus-within:border-primary-400 lg:w-[250px]">
              <i className="ri-search-line text-sm text-foreground-400" aria-hidden="true" />
              <input
                type="search"
                name="q"
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索影片、影人或资讯"
                aria-label="站内搜索"
                className="w-full bg-transparent text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none"
              />
              <button type="submit" className="cursor-pointer text-xs font-medium text-primary-700 whitespace-nowrap">
                搜索
              </button>
            </div>
          </form>

          <button
            type="button"
            onClick={() => setSearchOpen((open) => !open)}
            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-700 md:hidden"
            aria-label="打开搜索"
          >
            <i className="ri-search-line" aria-hidden="true" />
          </button>

          <button
            type="button"
            onClick={() => setMenuOpen((open) => !open)}
            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-800 lg:hidden"
            aria-label="打开菜单"
            aria-expanded={menuOpen}
          >
            <i className={menuOpen ? "ri-close-line text-lg" : "ri-menu-line text-lg"} aria-hidden="true" />
          </button>
        </div>
      </div>

      {searchOpen && (
        <div className="border-t border-background-200 bg-background-50 px-4 py-3 md:hidden">
          <form onSubmit={submitSearch} role="search">
            <div className="flex h-10 items-center gap-2 rounded-md border border-background-300 bg-background-100 px-3">
              <i className="ri-search-line text-sm text-foreground-400" aria-hidden="true" />
              <input
                ref={inputRef}
                type="search"
                name="q"
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索影片、影人或资讯"
                aria-label="站内搜索"
                className="w-full bg-transparent text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none"
              />
              <button type="submit" className="cursor-pointer text-sm font-medium text-primary-700 whitespace-nowrap">
                搜索
              </button>
            </div>
          </form>
        </div>
      )}

      {menuOpen && (
        <div className="border-t border-background-200 bg-background-50 px-4 pb-5 pt-3 lg:hidden">
          <div className="grid grid-cols-2 gap-2">
            {navItems.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={`flex items-center justify-between rounded-md border px-3 py-2.5 text-sm ${
                  isActive(item.to)
                    ? "border-accent-200 bg-accent-50 text-accent-700"
                    : "border-background-200 bg-background-100 text-foreground-700"
                }`}
              >
                {item.label}
                <i className="ri-arrow-right-s-line text-foreground-400" aria-hidden="true" />
              </Link>
            ))}
          </div>
          <div className="mt-3 flex items-center justify-between rounded-md bg-background-100 px-3 py-2 text-xs text-foreground-500">
            <span>全站搜索影视与资讯</span>
            <Link to="/search?q=悬疑" className="font-medium text-primary-700">
              试试「悬疑」
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}